import * as React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  REQUEST_STATUS_LABELS,
  useLinkRequestToPOLine,
  useProductRequests,
  type RequestStatus,
} from '@/hooks/useProductRequests'
import type { components } from '@/lib/api/generated'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/lib/dates'

type POLineItem = components['schemas']['POLineItemResponse']

/** Only requests nobody has put on a PO yet can be linked — the backend 409s
 *  on the rest. */
const LINKABLE: RequestStatus[] = ['OPEN', 'NEED_TO_SOURCE', 'SOURCED']

/** Past these, the customer has already been told it's on order. */
const ORDERED_PO_STATUSES = ['ORDERED', 'PARTIAL_ORDERED']

export function LinkRequestModal({
  poId,
  poStatus,
  line,
  onClose,
}: {
  poId: string
  poStatus: string
  line: POLineItem
  onClose: () => void
}) {
  const { data: requests = [], isLoading } = useProductRequests({
    variant_id: line.variant_id,
  })
  const link = useLinkRequestToPOLine()
  const busy = link.isPending
  const ordered = ORDERED_PO_STATUSES.includes(poStatus)

  const [selected, setSelected] = React.useState<number | null>(null)
  // Not-yet-ordered POs keep today's behaviour (email fires at mark-ordered);
  // an ordered PO already sent its emails, so a late link is opt-in.
  const [sendEmail, setSendEmail] = React.useState(!ordered)
  const [error, setError] = React.useState<string | null>(null)

  const capacity = (line.qty_ordered ?? 0) - (line.qty_adjusted ?? 0)
  const spokenFor = requests
    .filter((r) => r.po_id === poId && r.status !== 'CLOSED_NO_PURCHASE')
    .reduce((s, r) => s + r.qty, 0)
  const remaining = Math.max(0, capacity - spokenFor)
  const eligible = requests.filter((r) => LINKABLE.includes(r.status))

  async function handleConfirm() {
    const req = eligible.find((r) => r.id === selected)
    if (!req) return
    setError(null)
    try {
      const res = await link.mutateAsync({
        id: req.id,
        body: { po_id: poId, send_ordered_email: sendEmail },
      })
      toast.success(
        res.split_request_id == null
          ? `Linked ${req.customer_name} to ${res.po_id} — ${res.spoken_for} of ${res.qty_on_line} spoken for`
          : `Linked ${res.linked_qty} of ${req.qty} for ${req.customer_name} to ${res.po_id} — ${req.qty - res.linked_qty} left open as request #${res.split_request_id}`
      )
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link request')
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="text-base">Link a request to this line</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            {line.sku || '—'} · {line.product_title}
            {line.variant_title ? ` · ${line.variant_title}` : ''} · qty {capacity}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="text-xs text-[var(--ink-2)] tabular-nums" data-testid="spoken-for">
            {spokenFor} of {capacity} spoken for
            {remaining === 0 && (
              <span className="text-[var(--text-primary)]"> — no units left on this line</span>
            )}
          </div>

          {isLoading ? (
            <div className="text-xs text-[var(--mute)]">Loading requests…</div>
          ) : eligible.length === 0 ? (
            <div className="flex flex-col gap-1 border border-dashed border-[var(--line-warm)] py-3 text-center">
              <span className="text-xs text-[var(--mute)]">
                No open requests for this item.
              </span>
              <span className="text-[11px] text-[var(--mute)]">
                Requests typed in without a linked product won't appear here.
              </span>
            </div>
          ) : (
            <div className="flex max-h-[260px] flex-col overflow-auto border border-[var(--line-warm)]">
              {eligible.map((r) => {
                const full = remaining === 0
                // More than fits: the backend links what's left and splits
                // the rest off as a new request.
                const splits = !full && r.qty > remaining
                const grouped = r.request_group_id != null && r.group_count >= 2
                return (
                  <label
                    key={r.id}
                    className="flex items-start gap-2 border-t border-[var(--line-warm)] px-3 py-2 text-xs first:border-t-0"
                  >
                    <input
                      type="radio"
                      name="link-request"
                      className="mt-0.5"
                      checked={selected === r.id}
                      disabled={busy || full}
                      onChange={() => setSelected(r.id)}
                      aria-label={r.customer_name}
                    />
                    <div className="min-w-0 flex-1">
                      {/* Only the summary dims: the reason below must stay readable. */}
                      <div className={cn('flex items-baseline gap-2', full && 'opacity-50')}>
                        <span className="font-medium text-[var(--text-primary)]">
                          {r.customer_name}
                        </span>
                        <span className="tabular-nums text-[var(--ink-2)]">
                          qty {r.qty}
                        </span>
                        <span className="text-[var(--mute)]">
                          {REQUEST_STATUS_LABELS[r.status]}
                        </span>
                        <span className="ml-auto tabular-nums whitespace-nowrap text-[var(--mute)]">
                          {fmtDate(r.created_at, '—')}
                        </span>
                      </div>
                      {grouped && (
                        <div className="text-[11px] text-[var(--mute)]">
                          Logged together with {r.group_count - 1} other item(s)
                        </div>
                      )}
                      {splits && (
                        <div className="text-[11px] text-[var(--ink-2)]">
                          Links {remaining} of {r.qty} — {r.qty - remaining} stays open as a
                          new request
                        </div>
                      )}
                      {full && (
                        <div className="text-[11px] text-[var(--ink-2)]">
                          No units left on this line — {spokenFor} of {capacity} spoken for
                        </div>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5 text-xs text-[var(--text-primary)]">
              <input
                type="checkbox"
                checked={sendEmail}
                disabled={busy}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Send ordered notification
            </label>
            <span className="text-[11px] text-[var(--mute)]">
              {ordered
                ? "Emails the customer now that it's been ordered."
                : 'Emails the customer when this PO is marked ordered.'}{' '}
              The arrival email is always sent when the line is fully received.
            </span>
          </div>

          {error && (
            <div className="text-xs text-[var(--status-rejected-fg)]" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={busy || selected == null}
            onClick={() => void handleConfirm()}
          >
            {busy ? 'Linking…' : 'Link request'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
